/**
 * Phase-1 packaging — central route guard. Wraps the protected <Routes> once; for
 * the current path it resolves the gating ModuleKey and blocks render when the
 * customer is ENFORCING and not entitled. Platform Admin always passes; if the
 * entitlement summary hasn't loaded (fail-open) nothing is blocked.
 */
import React from 'react';
import { useLocation } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useEntitlements } from '../../hooks/useEntitlements';
import { moduleForPath } from '../../utils/entitlementAccess';

function NotEntitled({ moduleKey }) {
  return (
    <div className="flex flex-col items-center justify-center text-center px-6 py-20">
      <ShieldAlert size={40} className="text-v2-ink-muted mb-3" aria-hidden="true" />
      <div className="text-[16px] font-semibold text-v2-ink">This module isn’t in your plan</div>
      <div className="text-[13px] text-v2-ink-muted mt-1 max-w-md">
        {moduleKey ? `The ${moduleKey} module isn’t enabled for your organization. ` : ''}
        Ask your administrator to add it, or contact sales@prove7.ai to upgrade your plan.
      </div>
    </div>
  );
}

export function RequireModuleRoute({ children }) {
  const location = useLocation();
  const { currentRole } = useAuth();
  const { isUsable, enforcing, loaded } = useEntitlements();

  const isPlatformAdmin = currentRole === 'Platform Admin';
  const moduleKey = moduleForPath(location.pathname);
  const blocked = enforcing && loaded && !isPlatformAdmin && moduleKey && !isUsable(moduleKey);

  if (blocked) return <NotEntitled moduleKey={moduleKey} />;
  return children;
}

export default RequireModuleRoute;
